import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FAQAccordion from '@/components/feature/FAQAccordion';
import { siteInfo } from '@/mocks/siteData';

const aboutFAQ = [
  {
    question: '福利姬完全指南是一个什么样的平台？',
    answer: '福利姬完全指南是一个专注于解析"福利姬"网络文化现象的资讯平台。我们以学术性和客观性为宗旨，通过深度文章和行业分析，帮助读者全面理解这一网络现象，促进网络生态的健康发展。',
  },
  {
    question: '你们的内容是否可靠？',
    answer: '我们始终坚持客观、中立的立场。所有文章均经过严格的事实核查和多源交叉验证，确保信息的准确性和可靠性。我们不鼓励或推广任何违法违规行为，所有内容仅限于文化现象分析与资讯报道。',
  },
  {
    question: '我可以投稿或建议选题吗？',
    answer: '当然可以！我们欢迎读者提供反馈、建议选题或投稿。请通过站内联系方式与我们取得联系，我们会在收到信息后尽快回复。',
  },
];

const reputationItems = [
  { icon: 'ri-shield-check-line', title: '信息权威', desc: '内容经过严格的事实核查，引用多方权威来源，确保每篇文章的可靠性。' },
  { icon: 'ri-eye-line', title: '立场客观', desc: '坚持中立立场，不站队不偏袒，以分析性视角呈现网络文化现象的全貌。' },
  { icon: 'ri-time-line', title: '持续更新', desc: '保持高频内容更新，追踪行业最新动态，确保读者获取最前沿的资讯。' },
  { icon: 'ri-group-line', title: '读者至上', desc: '以读者需求为核心，持续优化内容质量和阅读体验。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d9qq3gue3oq4jqumtoj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([k]) => k !== 'phone_alt')
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });
      const text = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <div className="pt-16 bg-background-50">
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: '关于我们' },
        ]}
      />

      {/* Hero */}
      <section className="w-full py-12 md:py-18 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
            关于我们
          </span>
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
            福利姬完全指南
          </h1>
          <p className="text-sm md:text-base text-foreground-500 leading-relaxed max-w-xl mx-auto">
            {siteInfo.description}
          </p>
        </div>
      </section>

      {/* Brand Introduction */}
      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-start">
            <div>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-5">
                品牌故事
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  {siteInfo.name}成立于{siteInfo.founded}，是国内最早专注于福利姬网络文化现象研究的资讯平台之一。我们的团队由互联网文化研究者、社会学学者和资深媒体人组成，致力于以严谨的态度和专业的视角，为公众提供关于这一现象的客观分析与深度报道。
                </p>
                <p>
                  我们的使命是——{siteInfo.mission}
                </p>
                <p>
                  自成立以来，我们已累计发布深度文章36余篇，覆盖福利姬文化的起源发展、平台规则、内容生态、行业动态和文化背景五大维度，累计阅读量突破50万次，内容满意度高达98%。
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  {siteInfo.values.map((v, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-50 text-accent-700 rounded-full text-xs font-medium"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-check-line text-sm"></i>
                      </span>
                      {v}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <div className="w-full aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20conceptual%20art%20representing%20knowledge%20sharing%20and%20digital%20research%2C%20warm%20terracotta%20and%20golden%20tones%2C%20geometric%20shapes%20resembling%20open%20books%20and%20network%20connections%2C%20minimalist%20editorial%20style%2C%20professional%20clean%20composition&width=800&height=600&seq=about-brand-image&orientation=landscape"
                  alt="福利姬完全指南 - 品牌故事"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reputation */}
      <section className="w-full py-12 md:py-16 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-3">
              我们的承诺
            </span>
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
              信誉与价值观
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {reputationItems.map((item, idx) => (
              <div
                key={idx}
                className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70"
              >
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-accent-100 text-accent-600 rounded-xl">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="text-base font-bold font-heading text-foreground-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6 text-center">
            关于本站的常见问题
          </h2>
          <FAQAccordion items={aboutFAQ} />
        </div>
      </section>

      {/* Contact */}
      <section className="w-full py-12 md:py-16 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-start">
            <div>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-5">
                联系我们
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">{siteInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                    <p className="text-sm text-foreground-500">{siteInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                    <i className="ri-question-answer-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">反馈与建议</h4>
                    <p className="text-sm text-foreground-500">
                      欢迎通过邮件或微信向我们提供反馈和建议，我们会在48小时内回复。
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">
                  发送消息
                </h3>
                <form
                  data-readdy-form
                  action="https://readdy.ai/api/form/d9qq3gue3oq4jqumtoj0"
                  method="POST"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="about-name" className="block text-sm text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="about-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200/70 focus:outline-none focus:border-primary-400 text-foreground-900 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="about-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200/70 focus:outline-none focus:border-primary-400 text-foreground-900 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200/70 focus:outline-none focus:border-primary-400 text-foreground-900 transition-colors resize-none"
                      placeholder="请输入您想说的话，不超过500字"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    className="w-full px-6 py-2.5 bg-foreground-900 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-secondary-600 text-center">消息已发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-accent-600 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schema.org */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': '关于我们 - 福利姬完全指南',
        'description': siteInfo.description,
        'url': 'https://yikecongkuaika.cn/about',
        'about': {
          '@type': 'Organization',
          'name': siteInfo.name,
          'description': siteInfo.description,
          'foundingDate': siteInfo.founded,
          'email': siteInfo.contact.email,
        },
      }) }}></script>
    </div>
  );
}